import clsx from 'clsx';
import { isSameDay } from 'date-fns';
import { type ReactNode, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { SessionStatus } from '@/types';
import { Avatar, Button, Icon, Link, Paper, Typography } from '@/ui';
import { formatShortDayMonthDate } from '@/utils';

import type { SharedArtifact } from '@/store/api/sharedArtifactApi';

import { SCHEDULING_CANCELLATION_POLICY_URL } from '@/components/MentoCal/constants';
import {
  isMemberReflectionProfile,
  memberReflectionDescription,
  memberReflectionLabel,
} from '@/components/Sessions/memberReflectionProfiles';
import {
  buildMemberMomentsAnalyticsProperties,
  MEMBER_MOMENTS_EVENTS,
  momentsAnalytics,
} from '@/services/momentsAnalytics';
import { toDateSlug } from '@/utils/dateSlug';

export type MemberSessionAssetKind =
  'reflection' | 'summary' | 'topics' | 'transcript';
export type MemberSessionAssetState =
  'hidden' | 'pending' | 'ready' | 'unavailable';

export interface MemberSessionArtifactSummary {
  artifactId: string;
  profileSlug: string;
  title?: string;
  updatedAt: string;
  sessionDate?: string | null;
}

export interface MemberSessionTranscriptSummary {
  attachmentId: string;
  contentStatus?: string;
  displayName?: string;
}

export interface MemberSessionDetailPerson {
  avatarUrl?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}

export interface MemberSessionDetailSession {
  endedAt: string;
  id: string;
  location?: string | null;
  momentsSessionId?: string | null;
  startedAt: string;
  status: string;
  title: string;
}

export interface MemberSessionNote {
  id: string;
  body: string;
  createdAt?: string;
}

interface DerivedAssetRow {
  body: string;
  href?: string;
  icon: MemberSessionAssetKind;
  id: string;
  state: MemberSessionAssetState;
  title: string;
}

export type MemberSessionReadState = 'error' | 'loading' | 'ready';

export interface MemberSessionDetailRedesignProps {
  // Moments readiness reason for this session; final reasons such as
  // not_transcribed mean no reflection will ever be generated.
  artifactReadinessReason?: string;
  artifactReadinessStatus?: string;
  artifacts?: MemberSessionArtifactSummary[];
  // Artifact profiles whose generation has finally failed; Moments will not
  // retry them on its own, so they are shown as unavailable, not pending.
  failedArtifactProfiles?: string[];
  member: MemberSessionDetailPerson;
  sharedBy?: string;
  sharedSummary?: SharedArtifact | null;
  sharedSummaryHref?: string;
  notes?: MemberSessionNote[];
  notesContent?: ReactNode;
  now?: Date;
  onAddNote?: () => void;
  onCancel?: () => void;
  onJoin?: () => void;
  onReschedule?: () => void;
  previousSessionHadTranscript?: boolean;
  previousSessionReadinessReason?: string;
  session: MemberSessionDetailSession;
  sessionReadState?: MemberSessionReadState;
  transcripts?: MemberSessionTranscriptSummary[];
}

const SESSION_PREP_MEMBER_PROFILE_SLUG = 'session-prep-member';
const FAILED_REFLECTION_BODY =
  "We couldn't create your reflection for this session.";
const FAILED_TOPICS_BODY =
  "We couldn't create topic ideas from your previous session.";
const READY_CONTENT_STATUS = 'ready';

// Moments readiness reasons that are final: the session will never get a
// reflection, and the next session will never get topic ideas from it, so the
// member sees why instead of "pending" (MEN-5403).
const NEVER_GENERATED_BODIES: Record<
  string,
  { reflection: string; topics: string }
> = {
  no_conversation: {
    reflection:
      "There was no conversation in this session, so there's no reflection.",
    topics:
      'There was no conversation in the previous session, so there are no topic ideas.',
  },
  not_transcribed: {
    reflection: "This session wasn't transcribed, so there's no reflection.",
    topics:
      "The previous session wasn't transcribed, so there are no topic ideas.",
  },
};

const isDoneStatus = (status: string) => status === SessionStatus.DONE;
const isCancelledStatus = (status: string) =>
  status === SessionStatus.CANCELLED;

export const canJoinMemberSession = (
  session: MemberSessionDetailSession,
  now: Date = new Date(),
) =>
  !isDoneStatus(session.status) &&
  !isCancelledStatus(session.status) &&
  !!session.location?.startsWith('https:') &&
  isSameDay(new Date(session.startedAt), now);

const fullName = ({ firstName, lastName }: MemberSessionDetailPerson) =>
  [firstName, lastName].filter(Boolean).join(' ').trim();

const firstName = ({ firstName: name }: MemberSessionDetailPerson) =>
  name || 'your coach';

const artifactDateSlug = (
  artifact: MemberSessionArtifactSummary,
  fallback: string,
) =>
  toDateSlug(artifact.sessionDate || artifact.updatedAt || fallback, fallback);

const artifactHref = (
  kind: 'session-prep' | 'session-reflection',
  artifact: MemberSessionArtifactSummary,
  fallbackDate: string,
) =>
  `/m/artifacts/${kind}/${artifact.artifactId}/${artifactDateSlug(
    artifact,
    fallbackDate,
  )}`;

const transcriptHref = (
  session: MemberSessionDetailSession,
): string | undefined => {
  if (!session.momentsSessionId) {
    return undefined;
  }
  return `/m/session/${session.momentsSessionId}/transcript/${toDateSlug(
    session.startedAt,
    'session',
  )}`;
};

const shortTimeWithZone = (date: string, timeZone: string) =>
  new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
    timeZoneName: 'short',
  }).format(new Date(date));

const longMonthDate = (date: string) =>
  new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(date));

export const coarseCountdown = (startsAt: Date, now: Date): string => {
  const diffMs = startsAt.getTime() - now.getTime();
  if (diffMs <= 0) {
    return 'Now';
  }

  const minutes = Math.ceil(diffMs / 60000);
  if (minutes < 90) {
    return `In ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  }

  const hours = Math.ceil(minutes / 60);
  if (hours < 36) {
    return `In ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  }

  const days = Math.ceil(hours / 24);
  if (days < 14) {
    return `In ${days} ${days === 1 ? 'day' : 'days'}`;
  }

  const weeks = Math.ceil(days / 7);
  if (weeks < 8) {
    return `In ${weeks} ${weeks === 1 ? 'week' : 'weeks'}`;
  }

  const months = Math.ceil(days / 30);
  return `In ${months} ${months === 1 ? 'month' : 'months'}`;
};

export const deriveSessionTemporalState = (
  session: MemberSessionDetailSession,
  now: Date,
): 'cancelled' | 'completed' | 'future' | 'now' => {
  if (isCancelledStatus(session.status)) {
    return 'cancelled';
  }
  if (isDoneStatus(session.status)) {
    return 'completed';
  }

  const startsAt = new Date(session.startedAt);
  const endsAt = new Date(session.endedAt);
  if (now >= startsAt && now <= endsAt) {
    return 'now';
  }
  return 'future';
};

const readyTranscript = (transcripts: MemberSessionTranscriptSummary[]) =>
  transcripts.find(
    (transcript) => transcript.contentStatus === READY_CONTENT_STATUS,
  );

export const buildMemberSessionAssetRows = ({
  artifactReadinessReason,
  artifacts = [],
  artifactReadinessStatus,
  failedArtifactProfiles = [],
  sharedBy,
  sharedSummary,
  sharedSummaryHref,
  previousSessionHadTranscript,
  previousSessionReadinessReason,
  session,
  sessionReadState = 'ready',
  transcripts = [],
}: Pick<
  MemberSessionDetailRedesignProps,
  | 'artifactReadinessReason'
  | 'artifactReadinessStatus'
  | 'artifacts'
  | 'failedArtifactProfiles'
  | 'previousSessionHadTranscript'
  | 'previousSessionReadinessReason'
  | 'session'
  | 'sessionReadState'
  | 'sharedBy'
  | 'sharedSummary'
  | 'sharedSummaryHref'
  | 'transcripts'
>): DerivedAssetRow[] => {
  const reflection = artifacts.find((artifact) =>
    isMemberReflectionProfile(artifact.profileSlug),
  );
  const topics = artifacts.find(
    (artifact) => artifact.profileSlug === SESSION_PREP_MEMBER_PROFILE_SLUG,
  );
  const reflectionFailed =
    !reflection && failedArtifactProfiles.some(isMemberReflectionProfile);
  const topicsFailed =
    !topics &&
    failedArtifactProfiles.includes(SESSION_PREP_MEMBER_PROFILE_SLUG);
  const transcript = readyTranscript(transcripts);
  const hasAnyTranscript = transcripts.length > 0;
  const isPast = isDoneStatus(session.status);
  const isCancelled = isCancelledStatus(session.status);
  const isReadPending =
    sessionReadState !== 'ready' ||
    (!!artifactReadinessStatus && artifactReadinessStatus !== 'ready');
  const rows: DerivedAssetRow[] = [];
  const sharedSummaryRow: DerivedAssetRow | null =
    sharedSummary && sharedSummaryHref
      ? {
          body: `Shared by ${sharedBy || 'your coach'}`,
          href: sharedSummaryHref,
          icon: 'summary',
          id: 'shared-summary',
          state: 'ready',
          title: sharedSummary.title || 'Shared session summary',
        }
      : null;

  const previousSessionNeverGenerated = previousSessionReadinessReason
    ? NEVER_GENERATED_BODIES[previousSessionReadinessReason]
    : undefined;
  const previousSessionWasNotTranscribed =
    previousSessionHadTranscript === false;
  let missingTopicsBody =
    'Based on your previous session. Available a day before the session.';
  if (previousSessionNeverGenerated) {
    missingTopicsBody = previousSessionNeverGenerated.topics;
  } else if (previousSessionWasNotTranscribed) {
    missingTopicsBody =
      "The previous session wasn't transcribed, so there are no topic ideas.";
  }
  let topicsRow: DerivedAssetRow;
  if (topics) {
    topicsRow = {
      body: 'Based on your previous session',
      href: artifactHref('session-prep', topics, session.startedAt),
      icon: 'topics',
      id: 'topics',
      state: 'ready',
      title: 'Topic ideas',
    };
  } else if (topicsFailed) {
    topicsRow = {
      body: FAILED_TOPICS_BODY,
      icon: 'topics',
      id: 'topics',
      state: 'unavailable',
      title: 'Topic ideas',
    };
  } else {
    topicsRow = {
      body: missingTopicsBody,
      icon: 'topics',
      id: 'topics',
      state:
        previousSessionNeverGenerated || previousSessionWasNotTranscribed
          ? 'unavailable'
          : 'pending',
      title: 'Topic ideas',
    };
  }

  const neverGenerated = artifactReadinessReason
    ? NEVER_GENERATED_BODIES[artifactReadinessReason]
    : undefined;
  let reflectionUnavailableBody =
    "A short read on what you're really working on, not just what was said. Ready after you meet.";
  if (isCancelled) {
    reflectionUnavailableBody =
      "This session was cancelled, so there's no reflection.";
  } else if (reflectionFailed) {
    reflectionUnavailableBody = FAILED_REFLECTION_BODY;
  } else if (neverGenerated) {
    reflectionUnavailableBody = neverGenerated.reflection;
  } else if (isPast) {
    reflectionUnavailableBody =
      hasAnyTranscript || isReadPending
        ? 'Your session reflection will appear here when it is ready.'
        : "This session wasn't transcribed, so there's no reflection.";
  }

  const reflectionRow: DerivedAssetRow = reflection
    ? {
        body: `${memberReflectionDescription(reflection.profileSlug)}.`,
        href: artifactHref('session-reflection', reflection, session.startedAt),
        icon: 'reflection',
        id: 'reflection',
        state: 'ready',
        title: memberReflectionLabel(reflection.profileSlug),
      }
    : {
        body: reflectionUnavailableBody,
        icon: 'reflection',
        id: 'reflection',
        state:
          isCancelled ||
          reflectionFailed ||
          !!neverGenerated ||
          (isPast && !hasAnyTranscript && !isReadPending)
            ? 'unavailable'
            : 'pending',
        title: 'Reflection',
      };

  let transcriptRow: DerivedAssetRow | null = null;
  if (transcript) {
    const href = transcriptHref(session);
    transcriptRow = {
      body: 'Only you and your coach can see this',
      href,
      icon: 'transcript',
      id: 'transcript',
      state: href ? 'ready' : 'pending',
      title: 'Transcript',
    };
  } else if (hasAnyTranscript) {
    transcriptRow = {
      body: 'Your session transcript is being prepared.',
      icon: 'transcript',
      id: 'transcript',
      state: 'pending',
      title: 'Transcript',
    };
  }

  if (isCancelled) {
    if (sharedSummaryRow && !reflection) rows.push(sharedSummaryRow);
    rows.push(
      {
        ...topicsRow,
        body: 'This session was cancelled, so there are no topic ideas.',
        state: 'unavailable',
      },
      reflectionRow,
    );
    if (sharedSummaryRow && reflection) rows.push(sharedSummaryRow);
    return rows;
  }

  if (!isPast) {
    if (sharedSummaryRow && !reflection) rows.push(sharedSummaryRow);
    rows.push(topicsRow, reflectionRow);
    if (sharedSummaryRow && reflection) rows.push(sharedSummaryRow);
    return rows;
  }

  if (sharedSummaryRow && !reflection) rows.push(sharedSummaryRow);
  rows.push(reflectionRow);
  if (sharedSummaryRow && reflection) rows.push(sharedSummaryRow);
  if (transcriptRow) rows.push(transcriptRow);
  rows.push(topicsRow);
  return rows;
};

const ResourceIcon = ({
  disabled,
  kind,
}: {
  disabled: boolean;
  kind: MemberSessionAssetKind;
}) => {
  const stroke = disabled ? '#9EA0A3' : '#647A6D';
  if (kind === 'topics') {
    return (
      <svg
        aria-hidden="true"
        fill="none"
        height="18"
        viewBox="0 0 18 18"
        width="18"
      >
        <path
          d="M6.9 14.25h4.2"
          stroke={stroke}
          strokeLinecap="round"
          strokeWidth="1.5"
        />
        <path
          d="M7.5 16h3"
          stroke={stroke}
          strokeLinecap="round"
          strokeWidth="1.5"
        />
        <path
          d="M12.75 6.9c0-2.05-1.68-3.7-3.75-3.7s-3.75 1.65-3.75 3.7c0 1.18.55 2.23 1.42 2.91.46.36.83.85.83 1.43v.26h3v-.26c0-.58.37-1.07.83-1.43a3.64 3.64 0 0 0 1.42-2.91Z"
          stroke={stroke}
          strokeLinejoin="round"
          strokeWidth="1.5"
        />
        <path
          d="M9 5.25v2.4"
          stroke={stroke}
          strokeLinecap="round"
          strokeWidth="1.5"
        />
      </svg>
    );
  }
  if (kind === 'summary') {
    return (
      <Icon className={disabled ? 'text-[#9EA0A3]' : 'text-[#647A6D]'}>
        article
      </Icon>
    );
  }
  if (kind === 'reflection') {
    return (
      <svg
        aria-hidden="true"
        fill="none"
        height="18"
        viewBox="0 0 18 18"
        width="18"
      >
        <path
          d="M5.25 4.25c3.25 0 5.5 1.85 5.5 5.15v4.35"
          stroke={stroke}
          strokeLinecap="round"
          strokeWidth="1.5"
        />
        <path
          d="M10.75 8.75c0-2.9 1.9-4.5 4.75-4.5 0 3.05-1.55 5.05-4.75 5.05"
          stroke={stroke}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5"
        />
        <path
          d="M10.75 9.8c-3.6 0-5.95-1.95-6.25-5.55 3.8.05 6.25 2.05 6.25 5.55Z"
          stroke={stroke}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5"
        />
      </svg>
    );
  }
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height="18"
      viewBox="0 0 18 18"
      width="18"
    >
      <path
        d="M5.25 3.25h7.5v11.5h-7.5z"
        stroke={stroke}
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
      <path
        d="M7.25 6.25h3.5M7.25 8.75h3.5M7.25 11.25h2"
        stroke={stroke}
        strokeLinecap="round"
        strokeWidth="1.5"
      />
    </svg>
  );
};

const VideoIcon = () => (
  <svg
    aria-hidden="true"
    fill="none"
    height="16"
    viewBox="0 0 16 16"
    width="16"
  >
    <path
      d="M2.75 4.75h7.5v6.5h-7.5z"
      stroke="currentColor"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
    <path
      d="m10.25 7 3-1.75v5.5l-3-1.75V7Z"
      stroke="currentColor"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
);

const StatusBar = ({
  member,
  now,
  onCancel,
  onJoin,
  onReschedule,
  session,
}: Pick<
  MemberSessionDetailRedesignProps,
  'member' | 'now' | 'onCancel' | 'onJoin' | 'onReschedule' | 'session'
>) => {
  const referenceNow = now ?? new Date();
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
  const temporalState = deriveSessionTemporalState(session, referenceNow);
  const showActions = temporalState === 'future' || temporalState === 'now';
  const canJoin = canJoinMemberSession(session, referenceNow);
  const statusLabel = (() => {
    if (temporalState === 'completed') {
      return 'Completed';
    }
    if (temporalState === 'cancelled') {
      return 'Cancelled';
    }
    if (temporalState === 'now') {
      return 'Now';
    }
    return coarseCountdown(new Date(session.startedAt), referenceNow);
  })();

  return (
    <div className="rounded-2xl bg-[#FFF7ED] p-4">
      <div className="flex items-center gap-2 pb-4 text-[13px] leading-5 text-[#3F3F3F]">
        <Avatar
          className="shrink-0"
          firstName={member.firstName ?? ''}
          lastName={member.lastName ?? ''}
          size="xs"
          src={member.avatarUrl ?? ''}
        />
        <span>{fullName(member)} and you</span>
        <span aria-hidden="true">·</span>
        <span>{shortTimeWithZone(session.startedAt, timeZone)}</span>
        <span aria-hidden="true">·</span>
        <span>{statusLabel}</span>
      </div>
      {showActions && (
        <>
          <div className="flex flex-col gap-3 border-0 border-t border-solid border-cement-200 pt-4 sm:flex-row sm:items-center">
            {canJoin && (
              <Button
                className="gap-1.5 rounded-xl bg-[#63796D] px-3 py-2 text-sm font-bold normal-case text-white hover:bg-[#53675D]"
                variant="contained"
                onClick={onJoin}
              >
                <VideoIcon />
                Join Session
              </Button>
            )}
            <Button
              className="rounded-xl border-[#8D8D8D] px-4 py-2 text-sm font-bold normal-case text-[#272727]"
              variant="outlined"
              onClick={onReschedule}
            >
              Reschedule
            </Button>
            <Button
              className="p-2 text-sm font-bold normal-case text-[#272727]"
              variant="text"
              onClick={onCancel}
            >
              Cancel
            </Button>
          </div>
          <Typography
            className="mt-4 text-[13px] leading-5 text-[#4F4F4F]"
            variant="body2"
          >
            Need to make a change? Please reschedule or cancel 48 hours before
            the session start time.{' '}
            <Link
              className="font-semibold text-[#3F3F3F] underline"
              href={SCHEDULING_CANCELLATION_POLICY_URL}
              rel="noopener noreferrer"
              target="_blank"
            >
              Rescheduling & Cancellation Policy
            </Link>
            .
          </Typography>
        </>
      )}
    </div>
  );
};

const AssetRow = ({ row }: { row: DerivedAssetRow }) => {
  const disabled = row.state !== 'ready' || !row.href;
  const content = (
    <div
      className={clsx(
        'flex items-center justify-between border-0 border-b border-solid border-cement-200 py-5',
        disabled ? 'text-[#A0A1A3]' : 'text-[#292929]',
      )}
    >
      <div className="flex min-w-0 items-start gap-4">
        <div className="mt-0.5 flex size-5 shrink-0 items-center justify-center">
          <ResourceIcon disabled={disabled} kind={row.icon} />
        </div>
        <div className="min-w-0">
          <Typography
            className="text-[15px] font-bold leading-5"
            color="inherit"
            variant="body1"
          >
            {row.title}
          </Typography>
          <Typography
            className="line-clamp-2 text-[13px] leading-5"
            color="inherit"
            variant="body2"
          >
            {row.body}
          </Typography>
        </div>
      </div>
      {!disabled && (
        <span className="pl-4 text-lg leading-none text-[#647A6D]">›</span>
      )}
    </div>
  );

  if (disabled) {
    return content;
  }

  return (
    <Link
      color="inherit"
      component={RouterLink}
      to={row.href ?? ''}
      underline="none"
      onClick={() => {
        momentsAnalytics.track(
          MEMBER_MOMENTS_EVENTS.clickedSessionAsset,
          buildMemberMomentsAnalyticsProperties({
            assetKind: row.icon === 'topics' ? 'prep' : row.icon,
            destination: row.href,
            route: window.location.pathname,
            surface: 'session_detail',
          }),
        );
      }}
    >
      {content}
    </Link>
  );
};

const NotesPreview = ({
  member,
  notes,
  onAddNote,
}: {
  member: MemberSessionDetailPerson;
  notes: MemberSessionNote[];
  onAddNote?: () => void;
}) => (
  <div className="flex flex-col gap-4">
    {notes.length > 0 && (
      <>
        <Typography
          className="text-[13px] font-bold text-[#4F4F4F]"
          variant="body2"
        >
          Notes
        </Typography>
        <Paper className="overflow-hidden rounded-2xl" variant="outlined">
          {notes.map((note, index) => (
            <div
              key={note.id}
              className={clsx(
                'px-5 py-4',
                index > 0 && 'border-0 border-t border-solid border-cement-200',
              )}
            >
              <div className="mb-2 flex items-center gap-2 text-[13px] leading-5 text-[#595959]">
                <Avatar
                  firstName={member.firstName ?? ''}
                  lastName={member.lastName ?? ''}
                  size="xs"
                  src={member.avatarUrl ?? ''}
                />
                <span className="font-bold text-[#292929]">
                  {index === 0 ? firstName(member) : 'You'}
                </span>
                {note.createdAt && (
                  <span>{formatShortDayMonthDate(note.createdAt)}</span>
                )}
              </div>
              <Typography
                className="text-[15px] leading-6 text-[#292929]"
                variant="body2"
              >
                {note.body}
              </Typography>
            </div>
          ))}
        </Paper>
      </>
    )}
    <Paper className="rounded-2xl px-5 py-4" variant="outlined">
      <div className="flex min-h-14 items-center justify-between gap-4">
        <Typography className="text-[15px] text-[#9EA0A3]" variant="body2">
          Add a note...
        </Typography>
        <Button
          aria-label="Add note"
          className="size-7 min-w-0 rounded-full bg-[#63796D] p-0 text-white"
          variant="contained"
          onClick={onAddNote}
        >
          ↑
        </Button>
      </div>
    </Paper>
  </div>
);

export const MemberSessionDetailRedesign = ({
  artifactReadinessReason,
  artifactReadinessStatus,
  artifacts = [],
  failedArtifactProfiles,
  member,
  notes = [],
  notesContent,
  now,
  onAddNote,
  onCancel,
  onJoin,
  onReschedule,
  previousSessionHadTranscript,
  previousSessionReadinessReason,
  session,
  sessionReadState = 'ready',
  sharedBy,
  sharedSummary,
  sharedSummaryHref,
  transcripts = [],
}: MemberSessionDetailRedesignProps) => {
  const [clockNow, setClockNow] = useState(() => now ?? new Date());

  useEffect(() => {
    if (now) {
      setClockNow(now);
      return undefined;
    }

    const interval = window.setInterval(() => setClockNow(new Date()), 60000);
    return () => window.clearInterval(interval);
  }, [now]);

  const rows = buildMemberSessionAssetRows({
    artifactReadinessReason,
    artifactReadinessStatus,
    artifacts,
    failedArtifactProfiles,
    previousSessionHadTranscript,
    previousSessionReadinessReason,
    session,
    sessionReadState,
    sharedBy,
    sharedSummary,
    sharedSummaryHref,
    transcripts,
  }).map((row) => {
    if (row.id !== 'transcript' || row.state !== 'ready') {
      return row;
    }
    return {
      ...row,
      body: `Only you and ${firstName(member)} can see this`,
    };
  });
  const title = `${longMonthDate(session.startedAt)} Session with ${firstName(member)}`;

  return (
    <div className="min-h-screen bg-[#FCFAF7] px-4 py-7 text-[#292929]">
      <div className="mx-auto flex w-full max-w-[588px] flex-col gap-7">
        <Typography
          className="text-[30px] font-bold leading-10 tracking-[-0.02em] text-[#292929]"
          variant="h4"
        >
          {title}
        </Typography>

        <StatusBar
          member={member}
          now={clockNow}
          session={session}
          onCancel={onCancel}
          onJoin={onJoin}
          onReschedule={onReschedule}
        />

        <div>
          <Typography
            className="mb-3 text-[13px] font-bold text-[#4F4F4F]"
            variant="body2"
          >
            From this session
          </Typography>
          <div className="border-0 border-t border-solid border-cement-200">
            {rows.map((row) => (
              <AssetRow key={row.id} row={row} />
            ))}
          </div>
        </div>

        {notesContent ?? (
          <NotesPreview member={member} notes={notes} onAddNote={onAddNote} />
        )}
      </div>
    </div>
  );
};

export default MemberSessionDetailRedesign;
